import { useEffect, useMemo, useState } from "react";
import {
  CalendarRange,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Flag,
  Loader2,
  Plus,
  X,
  Ban,
  CheckCircle2,
  Users,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { Input } from "@/components/ui/Input";
import { FormField } from "@/components/ui/FormField";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import {
  ExternalSystemNotice,
  isExternalBookingSystem,
} from "@/components/dashboard/ExternalSystemNotice";
import { formatMoneyMinor } from "@/lib/money";
import { env } from "@/lib/env";

type SlotStatus = "open" | "blocked" | "booked" | "external";

interface Court {
  id: string;
  name: string;
  format: string;
  base_price_minor: number | null;
  currency: string;
}

interface Slot {
  id: string;
  court_id: string;
  starts_at: string;
  ends_at: string;
  status: SlotStatus;
  price_minor: number | null;
  external_source: string | null;
  notes: string | null;
}

const STATUS_META: Record<
  SlotStatus,
  { label: string; bg: string; color: string }
> = {
  open: {
    label: "Open",
    bg: "rgba(255,98,0,0.15)",
    color: "#FF6200",
  },
  blocked: {
    label: "Blocked",
    bg: "rgba(136,153,166,0.18)",
    color: "#8899A6",
  },
  booked: {
    label: "Booked",
    bg: "rgba(74,222,128,0.15)",
    color: "#4ade80",
  },
  external: {
    label: "External",
    bg: "rgba(96,165,250,0.15)",
    color: "#60a5fa",
  },
};

/**
 * Bookings + Availability tab. Weekly grid showing each court's slots,
 * click empty cells to create a slot, click existing slots to edit.
 * Venues managing their own availability do it here; venues synced
 * from external systems see `external` slots that were pulled in.
 */
// Shape of the match rollups (upcoming + past) shown around the weekly
// grid. Populated from the matches table with an embed for slot + court
// name + a count of active participants.
interface UpcomingMatch {
  id: string;
  status: string;
  target_players: number;
  unit_price_minor: number;
  currency: string;
  court: { name: string; format: string } | null;
  slot: { starts_at: string; ends_at: string } | null;
  active_count: { count: number }[] | null;
}
type PastMatch = UpcomingMatch & { confirmed_at: string | null; cancelled_at: string | null };

export function VenueBookingsTab() {
  const { profile } = useAuth();
  const venueAppId = profile?.venue_application_id;

  const [upcoming, setUpcoming] = useState<UpcomingMatch[] | null>(null);
  const [past, setPast] = useState<PastMatch[] | null>(null);
  const [pastExpanded, setPastExpanded] = useState(false);
  const [bookingSystem, setBookingSystem] = useState<string | null>(null);

  // Load booking_system to decide whether this is a Tier 1 venue. If
  // they're on an external system (MINDBODY, Club Automation, etc.)
  // we hide the manual availability calendar and show the read-only
  // notice instead - courts/slots flow through direct sync.
  useEffect(() => {
    if (!venueAppId) return;
    supabase
      .from("venue_applications")
      .select("booking_system")
      .eq("id", venueAppId)
      .single()
      .then(({ data }) => setBookingSystem(data?.booking_system ?? null));
  }, [venueAppId]);

  // Load the 5 nearest upcoming matches across all of this venue's
  // courts. Small query, cheap to re-run, gives the venue a "what's
  // coming in" glance without opening each court's calendar.
  useEffect(() => {
    if (!venueAppId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("matches")
        .select(`
          id, status, target_players, unit_price_minor, currency,
          court:courts!inner ( name, format, venue_application_id ),
          slot:availability_slots!matches_slot_id_fkey ( starts_at, ends_at ),
          active_count:match_participants ( count )
        `)
        .eq("court.venue_application_id", venueAppId)
        .in("status", ["pending", "confirmed"])
        .order("created_at", { ascending: false })
        .limit(20);
      // PostgREST doesn't support sorting by an embedded field, so we
      // pull a generous window and sort client-side by slot start time.
      const list = ((data ?? []) as UpcomingMatch[])
        .filter((m) => m.slot && new Date(m.slot.starts_at).getTime() > Date.now())
        .sort((a, b) => {
          const as = a.slot?.starts_at ?? "";
          const bs = b.slot?.starts_at ?? "";
          return as.localeCompare(bs);
        })
        .slice(0, 5);
      if (!cancelled) setUpcoming(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [venueAppId]);

  // Load past matches (confirmed/cancelled/completed). Cheap because
  // matches grow slowly per venue. Sorted client-side by slot start
  // descending - most recent first.
  useEffect(() => {
    if (!venueAppId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("matches")
        .select(`
          id, status, target_players, unit_price_minor, currency,
          confirmed_at, cancelled_at,
          court:courts!inner ( name, format, venue_application_id ),
          slot:availability_slots!matches_slot_id_fkey ( starts_at, ends_at ),
          active_count:match_participants ( count )
        `)
        .eq("court.venue_application_id", venueAppId)
        .in("status", ["confirmed", "cancelled", "completed"])
        .order("created_at", { ascending: false })
        .limit(50);
      const now = Date.now();
      const list = ((data ?? []) as PastMatch[])
        .filter(
          (m) =>
            m.status !== "confirmed" ||
            (m.slot && new Date(m.slot.starts_at).getTime() < now),
        )
        .sort((a, b) => {
          const as = a.slot?.starts_at ?? "";
          const bs = b.slot?.starts_at ?? "";
          return bs.localeCompare(as);
        });
      if (!cancelled) setPast(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [venueAppId]);

  const [courts, setCourts] = useState<Court[] | null>(null);
  const [selectedCourt, setSelectedCourt] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [editorSlot, setEditorSlot] = useState<Slot | "new" | null>(null);
  const [newSlotDate, setNewSlotDate] = useState<Date | null>(null);

  // Load courts once.
  useEffect(() => {
    if (!venueAppId) return;
    supabase
      .from("courts")
      .select("id, name, format, base_price_minor, currency")
      .eq("venue_application_id", venueAppId)
      .order("name")
      .then(({ data, error }) => {
        if (error) {
          toast.error(`Couldn't load courts: ${error.message}`);
          setCourts([]);
          return;
        }
        const list = (data ?? []) as Court[];
        setCourts(list);
        if (list.length > 0 && !selectedCourt) setSelectedCourt(list[0].id);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueAppId]);

  // Load slots whenever court or week changes.
  async function loadSlots() {
    if (!selectedCourt) return;
    setLoadingSlots(true);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const { data, error } = await supabase
      .from("availability_slots")
      .select("*")
      .eq("court_id", selectedCourt)
      .gte("starts_at", weekStart.toISOString())
      .lt("starts_at", weekEnd.toISOString())
      .order("starts_at");
    setLoadingSlots(false);
    if (error) {
      toast.error(error.message);
      setSlots([]);
      return;
    }
    setSlots((data ?? []) as Slot[]);
  }
  useEffect(() => {
    loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCourt, weekStart]);

  async function deleteSlot(slot: Slot) {
    if (slot.status === "booked") {
      return toast.error("Can't delete a booked slot. Cancel the match first.");
    }
    if (!window.confirm("Delete this slot?")) return;
    const { error } = await supabase
      .from("availability_slots")
      .delete()
      .eq("id", slot.id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    await loadSlots();
  }

  // Hoisted above the conditional returns below so hook order stays stable
  // across refetches - otherwise flipping between empty-courts and
  // populated-courts swaps the hook count and React panics.
  const week = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return days;
  }, [weekStart]);

  if (!courts) {
    return <PageLoader />;
  }

  // Tier 1 (external booking system) - hide the manual availability
  // calendar entirely and show the read-only notice. Courts and slots
  // sync from their booking system once direct sync ships.
  if (isExternalBookingSystem(bookingSystem)) {
    const systemName = (bookingSystem ?? "").trim();
    return (
      <div>
        <header className="mb-6">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
            Venue partner · Bookings
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">
            Bookings &amp; availability
          </h1>
        </header>
        <ExternalSystemNotice
          systemName={systemName}
          variant="availability"
        />
      </div>
    );
  }

  if (courts.length === 0) {
    return (
      <div>
        <header className="mb-6">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
            Venue partner · Bookings
          </p>
          <h1 className="text-3xl font-semibold tracking-tight">Bookings</h1>
        </header>
        <div className="rounded-2xl border border-dashed border-border bg-card/40 py-12 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-volt-subtle text-volt">
            <CalendarRange className="h-5 w-5" />
          </div>
          <p className="text-sm font-semibold">
            Add a court before setting availability
          </p>
          <p className="mx-auto mt-1 max-w-sm text-xs text-muted-foreground">
            Courts define what players can book. Head to the Courts tab
            first.
          </p>
          <Button asChild className="mt-4">
            <a href="../courts">Go to Courts</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Venue partner · Bookings
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">
          Bookings &amp; availability
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Add open slots for players to book, block time out for
          maintenance, and review confirmed games. Each cell is a slot;
          click one to edit.
        </p>
      </header>

      {/* Upcoming matches rollup - small "what's coming in" preview
          above the grid. Only renders once we've loaded data. */}
      {upcoming && upcoming.length > 0 && (
        <section className="mb-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Upcoming matches
          </h2>
          <ul className="space-y-2">
            {upcoming.map((m) => {
              const active = m.active_count?.[0]?.count ?? 0;
              const when = m.slot ? new Date(m.slot.starts_at) : null;
              return (
                <li
                  key={m.id}
                  className="flex flex-col gap-2 rounded-lg bg-background px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-semibold">
                      {m.court?.name} · {m.court?.format}
                      {m.status === "pending" && (
                        <span className="ml-2 rounded-full bg-[rgba(251,191,36,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#fbbf24]">
                          Filling
                        </span>
                      )}
                      {m.status === "confirmed" && (
                        <span className="ml-2 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
                          Confirmed
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {when ? formatMatchTime(when) : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="inline-flex items-center gap-1">
                      <Users className="h-3 w-3" /> {active} / {m.target_players}
                    </span>
                    <span className="font-semibold">
                      {formatMoneyMinor(m.unit_price_minor * m.target_players, m.currency)}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Confirmed matches auto-pay out via Stripe Connect at tip-off.
            Pending matches haven't hit their player target yet.
          </p>
        </section>
      )}

      {/* Court picker + week nav */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <select
          value={selectedCourt ?? ""}
          onChange={(e) => setSelectedCourt(e.target.value)}
          className="flex h-10 min-w-[180px] rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {courts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} · {c.format}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background hover:bg-accent"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="inline-flex h-9 items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent"
          >
            This week
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background hover:bg-accent"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm font-semibold sm:ml-3">
          {formatRange(weekStart, addDays(weekStart, 6))}
        </p>
        <div className="sm:ml-auto">
          <Button
            onClick={() => {
              setNewSlotDate(new Date());
              setEditorSlot("new");
            }}
          >
            <Plus className="h-4 w-4" />
            Add slot
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-4 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
        {(Object.keys(STATUS_META) as SlotStatus[]).map((s) => (
          <span key={s} className="inline-flex items-center gap-1">
            <span
              className="h-2 w-2 rounded-full"
              style={{ background: STATUS_META[s].color }}
            />
            {STATUS_META[s].label}
          </span>
        ))}
      </div>

      {/* Week grid */}
      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        <div className="grid grid-cols-7 border-b border-border bg-secondary/40 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {week.map((d) => (
            <div key={d.toISOString()} className="p-2">
              <div>
                {d.toLocaleDateString(undefined, { weekday: "short" })}
              </div>
              <div className="text-lg font-black text-foreground">
                {d.getDate()}
              </div>
            </div>
          ))}
        </div>
        {loadingSlots ? (
          <PageLoader label="Loading slots…" />
        ) : (
          <div className="grid min-h-[200px] grid-cols-7 divide-x divide-border">
            {week.map((day) => {
              const daySlots = (slots ?? []).filter((s) => sameDay(new Date(s.starts_at), day));
              return (
                <div
                  key={day.toISOString()}
                  className="flex flex-col gap-1 p-1.5"
                  onClick={() => {
                    setNewSlotDate(day);
                    setEditorSlot("new");
                  }}
                >
                  {daySlots.length === 0 ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewSlotDate(day);
                        setEditorSlot("new");
                      }}
                      className="flex min-h-[60px] w-full items-center justify-center rounded-md border border-dashed border-border/60 text-[10px] text-muted-foreground hover:border-volt hover:text-volt"
                    >
                      <Plus className="h-3 w-3 opacity-50" />
                    </button>
                  ) : (
                    daySlots.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditorSlot(s);
                        }}
                        className="group flex w-full flex-col items-start gap-0.5 rounded-md px-1.5 py-1 text-left text-[10px] transition-colors hover:brightness-110"
                        style={{
                          background: STATUS_META[s.status].bg,
                          color: STATUS_META[s.status].color,
                        }}
                      >
                        <span className="font-black">
                          {fmtTime(s.starts_at)}–{fmtTime(s.ends_at)}
                        </span>
                        <span className="text-[9px] font-semibold uppercase">
                          {STATUS_META[s.status].label}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Past matches - archival rollup. Collapsible because once a
          venue has run for a few months this list grows long, but the
          most recent few are useful at a glance. */}
      {past && past.length > 0 && (
        <section className="mt-8 rounded-2xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Past matches
            </h2>
            {past.length > 5 && (
              <button
                type="button"
                onClick={() => setPastExpanded((e) => !e)}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground"
              >
                {pastExpanded ? "Show recent only" : `Show all (${past.length})`}
              </button>
            )}
          </div>
          <ul className="space-y-2">
            {(pastExpanded ? past : past.slice(0, 5)).map((m) => (
              <PastMatchRow key={m.id} match={m} />
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Revenue figures show what your venue actually earned on the
            game (player_count × unit price). Cancelled matches are
            shown for reference; no money moved. Tap a row to see the
            roster and flag no-shows.
          </p>
        </section>
      )}

      {editorSlot && selectedCourt && (
        <SlotEditor
          slot={editorSlot === "new" ? null : editorSlot}
          courtId={selectedCourt}
          initialDay={newSlotDate ?? new Date()}
          courts={courts}
          onClose={() => setEditorSlot(null)}
          onSaved={async () => {
            setEditorSlot(null);
            await loadSlots();
          }}
          onDelete={
            editorSlot !== "new"
              ? async () => {
                  await deleteSlot(editorSlot);
                  setEditorSlot(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function SlotEditor({
  slot,
  courtId,
  initialDay,
  courts,
  onClose,
  onSaved,
  onDelete,
}: {
  slot: Slot | null;
  courtId: string;
  initialDay: Date;
  courts: Court[];
  onClose: () => void;
  onSaved: () => Promise<void>;
  onDelete?: () => Promise<void>;
}) {
  const court = courts.find((c) => c.id === courtId)!;
  const [status, setStatus] = useState<SlotStatus>(slot?.status ?? "open");
  const [startDate, setStartDate] = useState(
    slot ? toDateInput(new Date(slot.starts_at)) : toDateInput(initialDay),
  );
  const [startTime, setStartTime] = useState(
    slot ? toTimeInput(new Date(slot.starts_at)) : "18:00",
  );
  const [endTime, setEndTime] = useState(
    slot ? toTimeInput(new Date(slot.ends_at)) : "19:00",
  );
  const [price, setPrice] = useState(
    slot?.price_minor != null ? (slot.price_minor / 100).toFixed(2) : "",
  );
  const [notes, setNotes] = useState(slot?.notes ?? "");
  const [saving, setSaving] = useState(false);

  const readOnly = slot?.status === "booked" || slot?.status === "external";

  async function save(e: React.FormEvent) {
    e.preventDefault();
    const startsAt = new Date(`${startDate}T${startTime}`);
    const endsAt = new Date(`${startDate}T${endTime}`);
    if (!(startsAt.getTime() < endsAt.getTime())) {
      return toast.error("End must be after start.");
    }
    setSaving(true);
    const payload = {
      court_id: courtId,
      status,
      starts_at: startsAt.toISOString(),
      ends_at: endsAt.toISOString(),
      price_minor:
        price.trim() === "" ? null : Math.round(Number(price) * 100),
      notes: notes.trim() || null,
    };
    const { error } = slot
      ? await supabase.from("availability_slots").update(payload).eq("id", slot.id)
      : await supabase.from("availability_slots").insert(payload);
    setSaving(false);
    if (error) {
      return toast.error(error.message);
    }
    toast.success(slot ? "Saved" : "Slot added");
    await onSaved();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl border border-border bg-card p-6 shadow-2xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              {slot ? "Edit slot" : "New slot"}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {court.name} · {court.format}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {readOnly && (
          <div className="rounded-md border border-volt/30 bg-volt-subtle p-3 text-xs">
            {slot?.status === "booked" ? (
              <>
                <CheckCircle2 className="mr-1 inline h-3 w-3" />
                This slot is booked. To modify, cancel the match first.
              </>
            ) : (
              <>
                <Ban className="mr-1 inline h-3 w-3" />
                This slot was pulled in from your external booking
                system{slot?.external_source ? (
                  <>
                    {" "}(
                    <span className="font-mono">{slot.external_source}</span>
                    )
                  </>
                ) : null}
                . Manage it there to change.
              </>
            )}
          </div>
        )}

        <FormField label="Status" htmlFor="slot-status" required>
          <select
            id="slot-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as SlotStatus)}
            disabled={readOnly}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          >
            <option value="open">Open for booking</option>
            <option value="blocked">Blocked (maintenance etc)</option>
            {slot?.status === "booked" && <option value="booked">Booked</option>}
            {slot?.status === "external" && (
              <option value="external">External sync</option>
            )}
          </select>
        </FormField>

        <FormField label="Date" htmlFor="slot-date" required>
          <Input
            id="slot-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={readOnly}
          />
        </FormField>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Start" htmlFor="slot-start" required>
            <Input
              id="slot-start"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={readOnly}
            />
          </FormField>
          <FormField label="End" htmlFor="slot-end" required>
            <Input
              id="slot-end"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={readOnly}
            />
          </FormField>
        </div>

        {status === "open" && (
          <FormField
            label="Price override per player"
            htmlFor="slot-price"
            hint="Leave blank to use the court's base price."
          >
            <Input
              id="slot-price"
              type="number"
              step="0.01"
              min="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={
                court.base_price_minor != null
                  ? (court.base_price_minor / 100).toFixed(2)
                  : ""
              }
              disabled={readOnly}
            />
          </FormField>
        )}

        <FormField label="Notes (internal)" htmlFor="slot-notes">
          <Input
            id="slot-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Court closed for cleaning"
            disabled={readOnly}
          />
        </FormField>

        <div className="mt-2 flex items-center justify-end gap-2">
          {onDelete && !readOnly && (
            <Button
              type="button"
              variant="destructive"
              onClick={onDelete}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {!readOnly && (
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : slot ? (
                "Save changes"
              ) : (
                "Add slot"
              )}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

// --- past-match row + roster -----------------------------------------------
//
// A row in the "Past matches" rollup. Click to expand: lazy-loads the
// roster (display_name + email + flag count + already-flagged?) and
// surfaces a per-player "Flag no-show" button. Calls match-no-show-flag
// Edge Function which validates the caller is the venue admin.

interface RosterEntry {
  participant_id: string;
  user_id: string;
  status: string;
  display_name: string | null;
  flag_count: number;
  already_flagged: boolean;
}

function PastMatchRow({ match }: { match: PastMatch }) {
  const [expanded, setExpanded] = useState(false);
  const [roster, setRoster] = useState<RosterEntry[] | null>(null);
  const [flagging, setFlagging] = useState<string | null>(null);

  const active = match.active_count?.[0]?.count ?? 0;
  const when = match.slot ? new Date(match.slot.starts_at) : null;
  const flaggable =
    match.status === "confirmed" || match.status === "completed";

  async function loadRoster() {
    setRoster(null);
    // Pull captured/authorized participants + the existing no-show
    // flags for this match in two cheap queries; merge client-side.
    const [{ data: parts }, { data: flags }] = await Promise.all([
      supabase
        .from("match_participants")
        .select(
          "id, user_id, status, profile:profiles!match_participants_user_id_fkey(display_name, flag_count)",
        )
        .eq("match_id", match.id)
        .in("status", ["captured", "authorized"]),
      supabase
        .from("no_show_flags")
        .select("user_id")
        .eq("match_id", match.id),
    ]);
    const flagged = new Set((flags ?? []).map((f) => f.user_id as string));
    const list: RosterEntry[] = ((parts ?? []) as Array<{
      id: string;
      user_id: string;
      status: string;
      profile: { display_name: string | null; flag_count: number } | null;
    }>).map((p) => ({
      participant_id: p.id,
      user_id: p.user_id,
      status: p.status,
      display_name: p.profile?.display_name ?? null,
      flag_count: p.profile?.flag_count ?? 0,
      already_flagged: flagged.has(p.user_id),
    }));
    setRoster(list);
  }

  function toggle() {
    const next = !expanded;
    setExpanded(next);
    if (next && roster === null) loadRoster();
  }

  async function flagNoShow(entry: RosterEntry) {
    const notes = window.prompt(
      `Flag ${entry.display_name ?? "this player"} as a no-show? Optional notes for the audit log:`,
      "",
    );
    if (notes === null) return;
    setFlagging(entry.user_id);
    const { data: sess } = await supabase.auth.getSession();
    const jwt = sess?.session?.access_token;
    if (!jwt) {
      setFlagging(null);
      return toast.error("Session expired - refresh the page.");
    }
    try {
      const res = await fetch(
        `${env.supabaseUrl}/functions/v1/match-no-show-flag`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            match_id: match.id,
            user_id: entry.user_id,
            notes,
          }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(json?.error ?? `Flag failed (${res.status})`);
        return;
      }
      toast.success(
        `Flagged. New flag count for this player: ${json.new_flag_count ?? "-"}.`,
      );
      await loadRoster();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Network error");
    } finally {
      setFlagging(null);
    }
  }

  return (
    <li className="overflow-hidden rounded-lg bg-background text-sm">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-secondary/40"
      >
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {match.court?.name} · {match.court?.format}
            {match.status === "confirmed" && (
              <span className="ml-2 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
                Played
              </span>
            )}
            {match.status === "completed" && (
              <span className="ml-2 rounded-full bg-volt/15 px-2 py-0.5 text-[10px] font-bold text-volt">
                Completed
              </span>
            )}
            {match.status === "cancelled" && (
              <span className="ml-2 rounded-full bg-[rgba(248,113,113,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#f87171]">
                Cancelled
              </span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {when ? formatMatchTime(when) : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-xs">
          <span className="inline-flex items-center gap-1">
            <Users className="h-3 w-3" /> {active} / {match.target_players}
          </span>
          <span
            className={
              match.status === "cancelled"
                ? "font-semibold text-muted-foreground line-through"
                : "font-semibold"
            }
          >
            {formatMoneyMinor(match.unit_price_minor * active, match.currency)}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>
      {expanded && (
        <div className="border-t border-border/40 bg-card/40 px-3 py-3">
          {!flaggable && (
            <p className="text-xs text-muted-foreground">
              Roster is read-only for {match.status} matches.
            </p>
          )}
          {roster === null ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            </div>
          ) : roster.length === 0 ? (
            <p className="text-xs text-muted-foreground">No active roster.</p>
          ) : (
            <ul className="divide-y divide-border/40">
              {roster.map((r) => (
                <li
                  key={r.participant_id}
                  className="flex items-center justify-between gap-3 py-2 text-sm"
                >
                  <div>
                    <p className="font-medium">
                      {r.display_name ?? (
                        <span className="text-muted-foreground">
                          unnamed player
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {r.flag_count > 0
                        ? `${r.flag_count} flag${r.flag_count === 1 ? "" : "s"} on file`
                        : "Clean record"}
                    </p>
                  </div>
                  {flaggable && !r.already_flagged && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => flagNoShow(r)}
                      disabled={flagging !== null}
                    >
                      {flagging === r.user_id ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Flagging…
                        </>
                      ) : (
                        <>
                          <Flag className="h-3 w-3" />
                          Flag no-show
                        </>
                      )}
                    </Button>
                  )}
                  {r.already_flagged && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(248,113,113,0.15)] px-2 py-0.5 text-[10px] font-bold text-[#f87171]">
                      <Flag className="h-2.5 w-2.5" />
                      Flagged
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

// --- date helpers -----------------------------------------------------------

function mondayOf(d: Date) {
  const copy = new Date(d);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day; // if Sun, go back 6; else 1-day
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
}
function addDays(d: Date, n: number) {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + n);
  return copy;
}
function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function formatMatchTime(d: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

function formatRange(a: Date, b: Date) {
  const f: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" };
  const sameY = a.getFullYear() === b.getFullYear();
  return `${a.toLocaleDateString(undefined, f)} – ${b.toLocaleDateString(undefined, f)}${sameY ? ` ${a.getFullYear()}` : ""}`;
}
function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
function toDateInput(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function toTimeInput(d: Date) {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}
